// What a call's recording and transcript say on the call's card and in the customer's history
// (BL-083): who said each turn and when, where the words came from, and how long the call was. Calls
// are kept as text only: Play is only for audio kept from before.

export type TranscriptSource = 'twilio-transcription' | 'twilio-intelligence' | 'ours';
export interface Segment {
  speaker: string | null;
  startMs: number | null;
  endMs: number | null;
  text: string;
}
/** calls.transcript: null when the call has no Twilio recording. */
export interface CallRecording {
  recordingId: string;
  startedAt: string;
  durationSeconds: number;
  audio: boolean;
  /** Twilio is still making its words */
  pending: boolean;
  transcript: { source: TranscriptSource; provider: string; text: string; segments: Segment[] } | null;
}
export interface RecordingToMatch {
  recordingId: string;
  from: string;
  to: string;
  startedAt: string;
  durationSeconds: number;
  candidates: { callId: string; startedAt: string; customerName: string | null }[];
}

/** calls.recordingsToMatch's answer, a page at a time: `total` counts every one waiting. */
export interface RecordingsPage {
  recordings: RecordingToMatch[];
  total: number;
  next: string | null;
}

/** How many recordings to match are read at a time; the rest come on "Load more" (list caps, BL-110). */
export const MATCH_PAGE = 50;

/** Only those more than one call fits (the server picks them), a page at a time. */
export const MATCH_INPUT = { match: 'review', limit: MATCH_PAGE } as const;

/** A page's recordings as a list's rows: each keyed by its recording. */
export const matchRows = (p: RecordingsPage): (RecordingToMatch & { id: string })[] =>
  p.recordings.map((r) => ({ ...r, id: r.recordingId }));

/** "Twilio's transcript", "Twilio Voice Intelligence", or ours: "Transcribed here". */
export const sourceWord = (s: TranscriptSource) =>
  s === 'twilio-transcription'
    ? 'Twilio’s transcript'
    : s === 'twilio-intelligence'
      ? 'Twilio Voice Intelligence'
      : 'Transcribed here';

/** "1 min 35 s", "40 s". */
export const lengthText = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? (s ? `${m} min ${s} s` : `${m} min`) : `${s} s`;
};

/** "0:12", "4:05": where in the call a turn starts. */
export const atText = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Each speaker's turn, a run of their sentences joined, with where it starts in the call (null when
 * the words carry no times); an unnamed speaker has no name shown.
 */
export function turns(
  segments: readonly Segment[],
): { speaker: string | null; text: string; at: string | null }[] {
  const out: { speaker: string | null; text: string; at: string | null }[] = [];
  for (const s of segments) {
    const text = s.text.trim();
    if (!text) continue;
    const speaker = s.speaker?.replace(/^channel (\d+)$/, 'Speaker $1') ?? null;
    const last = out[out.length - 1];
    if (last && last.speaker === speaker) last.text = `${last.text} ${text}`;
    else out.push({ speaker, text, at: s.startMs === null ? null : atText(s.startMs) });
  }
  return out;
}

/** What the card says when there are no words: Twilio's are on their way, or there are none. */
export const noWords = (rec: Pick<CallRecording, 'pending'>) =>
  rec.pending ? 'Transcript on its way.' : 'No transcript for this call.';
